import React, { useEffect, useState, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon, ChevronDownIcon, ArrowRightIcon, MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
type PlatformItem = {
  label: string;
  to: string;
};
const PLATFORM_BCITI_LEFT: PlatformItem[] = [{
  label: 'Identity',
  to: '/core-infrastructure'
}, {
  label: 'Digital Card',
  to: '/digital-card'
}, {
  label: 'Requests',
  to: '/requests'
}, {
  label: 'Smart Water',
  to: '/smart-water'
}];
const PLATFORM_BCITI_RIGHT: PlatformItem[] = [{
  label: 'Communications',
  to: '/communications'
}, {
  label: 'Collect',
  to: '/collect'
}, {
  label: 'Analytics',
  to: '/analytics'
}];
const PLATFORM_BCITI_ALL = [...PLATFORM_BCITI_LEFT, ...PLATFORM_BCITI_RIGHT];
const PLATFORM_ATNA: PlatformItem[] = [{
  label: 'ATNA Assistant',
  to: '/atna'
}, {
  label: 'Insights',
  to: '/atna'
}, {
  label: 'Recommendations',
  to: '/atna'
}, {
  label: 'Actions',
  to: '/atna'
}];
const DIRECT_LINKS: Record<string, string> = {
  Customers: '/brossard',
  Trust: '/trust',
  Company: '/about'
};
const INDUSTRIES_MENU = [{
  title: 'Cities & Government',
  titleFr: 'Villes & Gouvernement',
  desc: 'Connect citizens, communications and services.',
  descFr: 'Connectez les citoyens, les communications et les services.',
  slug: 'cities-government',
  featured: true
}, {
  title: 'Airports & Transportation',
  titleFr: 'Aéroports & Transport',
  desc: 'Create trusted traveler experiences.',
  descFr: 'Créez des expériences de voyage de confiance.',
  slug: 'airports'
}, {
  title: 'Telecommunications & Media',
  titleFr: 'Télécom & Médias',
  desc: 'Turn subscribers into engaged communities.',
  descFr: 'Transformez les abonnés en communautés engagées.',
  slug: 'telecom'
}, {
  title: 'Health Networks',
  titleFr: 'Réseaux de Santé',
  desc: 'Coordinate identity, consent and services.',
  descFr: "Coordonnez l'identité, le consentement et les services.",
  slug: 'health'
}, {
  title: 'Universities & Campuses',
  titleFr: 'Universités & Campus',
  desc: 'Unify student identity and campus services.',
  descFr: "Unifiez l'identité étudiante et les services du campus.",
  slug: 'universities'
}];
const COMPANY_MENU = ['About BCITI', 'Leadership', 'News', 'Contact'];
const NAV_LABELS_EN: Record<string, string> = {
  Customers: 'Case Study'
};
const NAV_LABELS_FR: Record<string, string> = {
  Platform: 'Plateforme',
  Industries: 'Secteurs',
  Customers: 'Étude de cas',
  Trust: 'Confiance',
  Company: 'Entreprise'
};
const PLATFORM_LABELS_FR: Record<string, string> = {
  Identity: 'Identité',
  'Digital Card': 'Carte numérique',
  Requests: 'Requêtes',
  Collect: 'Collecte',
  Analytics: 'Analytique',
  'Smart Water': 'Compteurs intelligents'
};
const ATNA_QUESTION_LABELS_FR: Record<string, string> = {
  'Improve emergency communications': "Améliorer les communications d'urgence",
  'Apply this model to an airport': 'Appliquer ce modèle à un aéroport',
  'What to deploy first': 'Quoi déployer en premier'
};
export function Header() {
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  const navLabel = (item: string) => lang === 'FR' ? NAV_LABELS_FR[item] ?? item : NAV_LABELS_EN[item] ?? item;
  const platformLabel = (label: string) => lang === 'FR' ? PLATFORM_LABELS_FR[label] ?? label : label;
  const atnaQuestionLabel = (label: string) => lang === 'FR' ? ATNA_QUESTION_LABELS_FR[label] ?? label : label;
  const industryTitle = (ind: (typeof INDUSTRIES_MENU)[number]) => lang === 'FR' ? ind.titleFr : ind.title;
  const industryDesc = (ind: (typeof INDUSTRIES_MENU)[number]) => lang === 'FR' ? ind.descFr : ind.desc;
  const companyLabel = (label: string) => label === 'About BCITI' ? t('About BCITI', 'À propos de BCITI') : label;
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({
    Platform: true,
    Industries: true
  });
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    setActiveMenu(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const goToAnchor = (path: string, hash?: string) => {
    setActiveMenu(null);
    navigate(path);
    if (!hash) return;
    setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 250);
  };
  const ATNA_QUESTIONS: {
    label: string;
    path: string;
    hash?: string;
  }[] = [{
    label: 'Improve emergency communications',
    path: '/communications',
    hash: 'emergency'
  }, {
    label: 'Apply this model to an airport',
    path: '/request-demo'
  }, {
    label: 'What to deploy first',
    path: '/platform',
    hash: 'stack'
  }];
  const toggleMobileSection = (section: string) => {
    setMobileExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  let timeoutId: NodeJS.Timeout;
  const handleMouseEnter = (menu: string) => {
    clearTimeout(timeoutId);
    setActiveMenu(menu);
  };
  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };
  return <motion.header initial={{
    opacity: 0,
    y: -20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6
  }} className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E4E6EC]" onMouseLeave={handleMouseLeave} data-id="element-350">
      <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between" data-id="element-351">
        <Link to="/" className="flex items-center z-50 relative" aria-label={t('BCITI — Home', 'BCITI — Accueil')} onClick={() => setActiveMenu(null)} data-id="element-352">
          <img src="https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png" alt="BCITI" className="h-[5.25rem] w-auto object-contain" data-id="element-353" />
          <span className="ml-4 mr-1 text-[#121212]/25 font-sans text-3xl font-light select-none" aria-hidden="true" data-id="element-354">
            |
          </span>
          <img src="https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png" alt="ATNA" className="h-[4.5rem] w-auto object-contain" data-id="element-355" />
        </Link>

        <nav className="hidden lg:flex items-center h-full" data-id="element-356">
          {['Platform', 'Industries', 'Customers', 'Trust', 'Company'].map(item => {
          const hasDropdown = item === 'Platform' || item === 'Industries';
          if (!hasDropdown) {
            return <Link key={item} to={DIRECT_LINKS[item]} onMouseEnter={() => setActiveMenu(null)} className="h-full flex items-center px-4 font-sans text-sm font-medium text-[#121212]/70 hover:text-[#121212] transition-colors" data-id="element-357">
                    {navLabel(item)}
                  </Link>;
          }
          return <div key={item} className="h-full flex items-center px-4 cursor-pointer" onMouseEnter={() => handleMouseEnter(item)} data-id="element-358">
                  <span className={`font-sans text-sm font-medium flex items-center gap-1 transition-colors ${activeMenu === item ? 'text-[#4F46E5]' : 'text-[#121212]/70 hover:text-[#121212]'}`} data-id="element-359">
                    {navLabel(item)}
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${activeMenu === item ? 'rotate-180 text-[#4F46E5]' : 'text-[#121212]/40'}`} data-id="element-360" />
                  </span>
                </div>;
        })}
        </nav>

        <div className="hidden lg:flex items-center gap-6 z-50 relative" data-id="element-361">
          <div className="flex items-center font-sans text-xs font-semibold tracking-wider" aria-label="Language" data-id="element-362">
            {(['FR', 'EN'] as const).map((code, i) => <Fragment key={code} data-id="element-363">
                {i === 1 && <span className="mx-2 text-[#121212]/20" data-id="element-364">|</span>}
                <button onClick={() => setLang(code)} aria-pressed={lang === code} className={lang === code ? 'text-[#121212]' : 'text-[#121212]/40 hover:text-[#121212]/70 transition-colors'} data-id="element-365">
                  {code}
                </button>
              </Fragment>)}
          </div>

          <Link to="/request-demo" className="flex items-center gap-2 px-6 py-2.5 bg-[#4F46E5] text-white text-sm font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-366">
            {t('Request a Demo', 'Demander une démo')}
            <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-367" />
          </Link>
        </div>

        <button className="lg:hidden z-50 relative p-2 -mr-2 text-[#121212]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-368">
          {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-369" /> : <MenuIcon className="w-6 h-6" data-id="element-370" />}
        </button>
      </div>

      <AnimatePresence data-id="element-371">
        {activeMenu && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} className="absolute top-full left-0 right-0 bg-white border-b border-[#E4E6EC] shadow-xl shadow-black/5 overflow-hidden" onMouseEnter={() => handleMouseEnter(activeMenu)} onMouseLeave={handleMouseLeave} data-id="element-372">
            <div className="max-w-[1200px] mx-auto px-6 py-10" data-id="element-373">
              {activeMenu === 'Platform' && <div data-id="element-374">
                  <div className="grid grid-cols-2 gap-16 mb-10" data-id="element-375">
                    <div data-id="element-376">
                      <h3 className="font-serif text-2xl text-[#121212] mb-6" data-id="element-377">
                        {t('BCITI Platform', 'Plateforme BCITI')}
                      </h3>
                      <Link to="/platform" onClick={() => setActiveMenu(null)} className="group block mb-5 pb-5 border-b border-[#E4E6EC]" data-id="element-378">
                        <div className="font-sans text-sm font-semibold text-[#121212] group-hover:text-[#4F46E5] transition-colors" data-id="element-379">
                          {t('Overview', "Vue d'ensemble")}
                        </div>
                        <div className="font-sans text-xs text-[#121212]/50 mt-1" data-id="element-380">
                          {t('Everything connects to a verified core.', 'Tout converge vers un noyau vérifié.')}
                        </div>
                      </Link>
                      <div className="grid grid-cols-2 gap-x-8" data-id="element-381">
                        <ul className="space-y-4" data-id="element-382">
                          {PLATFORM_BCITI_LEFT.map(item => <li key={item.label} data-id="element-383">
                              <Link to={item.to} onClick={() => setActiveMenu(null)} className="font-sans text-sm text-[#121212]/70 hover:text-[#4F46E5] transition-colors" data-id="element-384">
                                {platformLabel(item.label)}
                              </Link>
                            </li>)}
                        </ul>
                        <ul className="space-y-4" data-id="element-385">
                          {PLATFORM_BCITI_RIGHT.map(item => <li key={item.label} data-id="element-386">
                              <Link to={item.to} onClick={() => setActiveMenu(null)} className="font-sans text-sm text-[#121212]/70 hover:text-[#4F46E5] transition-colors" data-id="element-387">
                                {platformLabel(item.label)}
                              </Link>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                    <div data-id="element-388">
                      <h3 className="font-serif text-2xl text-[#121212] mb-6 flex items-center gap-2" data-id="element-389">
                        {t('ATNA Intelligence', 'Intelligence ATNA')}
                        <span className="inline-block w-1.5 h-1.5 bg-[#4F46E5]" data-id="element-390" />
                      </h3>
                      <div className="font-sans text-sm font-semibold tracking-wide text-[#121212]/50 mb-3" data-id="element-391">
                        {t('Ask ATNA', 'Demander à ATNA')}
                      </div>
                      <div className="flex flex-col gap-3" data-id="element-392">
                        {ATNA_QUESTIONS.map(q => <button key={q.label} type="button" onClick={() => goToAnchor(q.path, q.hash)} className="group flex items-center justify-between gap-3 border border-[#E4E6EC] px-4 py-3 text-left font-sans text-sm text-[#121212]/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-393">
                            {atnaQuestionLabel(q.label)}
                            <ArrowRightIcon className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-394" />
                          </button>)}
                      </div>
                      <Link to="/atna" className="inline-flex items-center gap-2 mt-5 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-395">
                        {t('Explore ATNA', 'Explorer ATNA')}{' '}
                        <ArrowRightIcon className="w-4 h-4" data-id="element-396" />
                      </Link>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-6 border-t border-[#E4E6EC]" data-id="element-397">
                    <p className="font-sans text-sm font-medium text-[#121212]/50" data-id="element-398">
                      {t('Identity powers communications. Communications power services. Services power intelligence.', "L'identité alimente les communications. Les communications alimentent les services. Les services alimentent l'intelligence.")}
                    </p>
                    <Link to="/platform" className="flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-399">
                      {t('Explore the Platform', 'Explorer la plateforme')}{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-400" />
                    </Link>
                  </div>
                </div>}

              {activeMenu === 'Industries' && <div data-id="element-401">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10" data-id="element-402">
                    {INDUSTRIES_MENU.map((ind, i) => <Link key={ind.title} to={`/industries/${ind.slug}`} onClick={() => setActiveMenu(null)} className={`group block cursor-pointer p-6 border transition-colors ${ind.featured ? 'border-[#4F46E5]/20 bg-[#4F46E5]/[0.02] hover:border-[#4F46E5]/40 hover:bg-[#4F46E5]/[0.05] md:col-span-1 md:row-span-2' : 'border-[#E4E6EC] hover:border-[#4F46E5] hover:bg-[#4F46E5]/[0.03]'}`} data-id="element-403">
                        <h3 className={`font-serif text-xl mb-2 flex items-center justify-between transition-colors ${ind.featured ? 'text-[#4F46E5]' : 'text-[#121212] group-hover:text-[#4F46E5]'}`} data-id="element-404">
                          {industryTitle(ind)}
                          <ArrowUpRightIcon className={`w-4 h-4 opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all text-[#4F46E5]`} data-id="element-405" />
                        </h3>
                        <p className="font-sans text-sm text-[#121212]/60 leading-relaxed" data-id="element-406">
                          {industryDesc(ind)}
                        </p>
                      </Link>)}
                  </div>
                  <div className="flex justify-end pt-6 border-t border-[#E4E6EC]" data-id="element-407">
                    <Link to="/" className="flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-408">
                      {t('View all industries', 'Voir tous les secteurs')}{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-409" />
                    </Link>
                  </div>
                </div>}

              {activeMenu === 'Customers' && <div data-id="element-410">
                  <div className="grid grid-cols-2 gap-16 mb-10" data-id="element-411">
                    <div data-id="element-412">
                      <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 mb-6 uppercase" data-id="element-413">
                        {t('Featured Case Study', 'Étude de cas en vedette')}
                      </div>
                      <Link to="/brossard" className="group block border border-[#E4E6EC] p-8 hover:border-[#4F46E5]/30 transition-colors" data-id="element-414">
                        <h3 className="font-serif text-3xl text-[#121212] mb-6 group-hover:text-[#4F46E5] transition-colors" data-id="element-415">
                          City of Brossard
                        </h3>
                        <div className="grid grid-cols-3 gap-4" data-id="element-416">
                          <div data-id="element-417">
                            <div className="font-serif text-xl text-[#121212] mb-1" data-id="element-418">
                              88,266
                            </div>
                            <div className="font-sans text-xs text-[#121212]/50" data-id="element-419">
                              {t('Verified Identities', 'Identités vérifiées')}
                            </div>
                          </div>
                          <div data-id="element-420">
                            <div className="font-serif text-xl text-[#121212] mb-1" data-id="element-421">
                              78%
                            </div>
                            <div className="font-sans text-xs text-[#121212]/50" data-id="element-422">
                              {t('Population Coverage', 'Couverture de la population')}
                            </div>
                          </div>
                          <div data-id="element-423">
                            <div className="font-serif text-xl text-[#121212] mb-1" data-id="element-424">
                              {t('Since 2017', 'Depuis 2017')}
                            </div>
                            <div className="font-sans text-xs text-[#121212]/50" data-id="element-425">
                              {t('In Production', 'En production')}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div data-id="element-426">
                      <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 mb-6 uppercase" data-id="element-427">
                        {t('More Customer Stories', 'Plus de témoignages clients')}
                      </div>
                      <ul className="space-y-4" data-id="element-428">
                        {['Municipalities', 'Associations', 'Public Organizations'].map(item => <li key={item} data-id="element-429">
                            <Link to="/" className="font-serif text-xl text-[#121212]/80 hover:text-[#4F46E5] transition-colors flex items-center justify-between group" data-id="element-430">
                              {lang === 'FR' ? item === 'Municipalities' ? 'Municipalités' : item === 'Associations' ? 'Associations' : 'Organismes publics' : item}
                              <ArrowRightIcon className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" data-id="element-431" />
                            </Link>
                          </li>)}
                      </ul>
                    </div>
                  </div>
                  <div className="flex justify-end pt-6 border-t border-[#E4E6EC]" data-id="element-432">
                    <Link to="/" className="flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-433">
                      {t('Explore customer results', 'Explorer les résultats clients')}{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-434" />
                    </Link>
                  </div>
                </div>}

              {activeMenu === 'Company' && <div data-id="element-435">
                  <div className="grid grid-cols-4 gap-8 mb-10" data-id="element-436">
                    {COMPANY_MENU.map(item => <Link key={item} to="/" className="group block p-6 border border-[#E4E6EC] hover:border-[#4F46E5]/30 transition-colors" data-id="element-437">
                        <h3 className="font-serif text-xl text-[#121212] group-hover:text-[#4F46E5] transition-colors" data-id="element-438">
                          {companyLabel(item)}
                        </h3>
                      </Link>)}
                  </div>
                  <div className="flex justify-end pt-6 border-t border-[#E4E6EC]" data-id="element-439">
                    <Link to="/" className="flex items-center gap-2 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-440">
                      {t('Learn more about BCITI', 'En savoir plus sur BCITI')}{' '}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-441" />
                    </Link>
                  </div>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-442">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: '100vh'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-[#E4E6EC] overflow-y-auto pb-24" data-id="element-443">
            <div className="p-6 border-b border-[#E4E6EC] bg-white sticky top-0 z-10" data-id="element-444">
              <Link to="/request-demo" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#4F46E5] text-white text-sm font-medium rounded-none" data-id="element-445">
                {t('Request a Demo', 'Demander une démo')}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-446" />
              </Link>
            </div>

            <div className="px-6 py-4" data-id="element-447">
              {['Platform', 'Industries', 'Customers', 'Trust', 'Company'].map(item => {
            const hasDropdown = item === 'Platform' || item === 'Industries';
            if (!hasDropdown) {
              return <div key={item} className="border-b border-[#E4E6EC] last:border-0" data-id="element-448">
                        <Link to={DIRECT_LINKS[item]} onClick={() => setMobileMenuOpen(false)} className="block py-5 font-serif text-xl text-[#121212]" data-id="element-449">
                          {navLabel(item)}
                        </Link>
                      </div>;
            }
            return <div key={item} className="border-b border-[#E4E6EC] last:border-0" data-id="element-450">
                      <button onClick={() => toggleMobileSection(item)} className="flex items-center justify-between w-full py-5 text-left" data-id="element-451">
                        <span className="font-serif text-xl text-[#121212]" data-id="element-452">
                          {navLabel(item)}
                        </span>
                        <ChevronDownIcon className={`w-5 h-5 text-[#121212]/40 transition-transform ${mobileExpanded[item] ? 'rotate-180' : ''}`} data-id="element-453" />
                      </button>
                      <AnimatePresence data-id="element-454">
                        {mobileExpanded[item] && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-455">
                            <div className="pb-6 pl-4 space-y-4 border-l-2 border-[#E4E6EC] ml-2" data-id="element-456">
                              {item === 'Platform' && <>
                                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-2" data-id="element-457">
                                    {t('BCITI Platform', 'Plateforme BCITI')}
                                  </div>
                                  {PLATFORM_BCITI_ALL.map(link => <Link key={link.label} to={link.to} onClick={() => setMobileMenuOpen(false)} className="block font-sans text-sm text-[#121212]/70" data-id="element-458">
                                      {platformLabel(link.label)}
                                    </Link>)}
                                  <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mt-6 mb-2" data-id="element-459">
                                    {t('ATNA Intelligence', 'Intelligence ATNA')}
                                  </div>
                                  {PLATFORM_ATNA.map(link => <Link key={link.label} to={link.to} className="block font-sans text-sm text-[#121212]/70" data-id="element-460">
                                      {link.label}
                                    </Link>)}
                                </>}
                              {item === 'Industries' && <>
                                  {INDUSTRIES_MENU.map(ind => <Link key={ind.title} to={`/industries/${ind.slug}`} onClick={() => setMobileMenuOpen(false)} className={`block font-sans text-sm ${ind.featured ? 'text-[#4F46E5] font-medium' : 'text-[#121212]/70'}`} data-id="element-461">
                                      {industryTitle(ind)}
                                    </Link>)}
                                </>}
                              {item === 'Customers' && <>
                                  <Link to="/brossard" className="block font-sans text-sm text-[#4F46E5] font-medium" data-id="element-462">
                                    City of Brossard
                                  </Link>
                                </>}
                              {item === 'Company' && <>
                                  {COMPANY_MENU.map(link => <Link key={link} to="/" className="block font-sans text-sm text-[#121212]/70" data-id="element-463">
                                      {companyLabel(link)}
                                    </Link>)}
                                </>}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
          })}
            </div>

            <div className="px-6 py-8 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-464">
              <div className="flex items-center gap-4 font-sans text-sm font-medium" data-id="element-465">
                <span className="text-[#121212]/40" data-id="element-466">
                  {t('Language:', 'Langue :')}
                </span>
                {(['FR', 'EN'] as const).map(code => <button key={code} onClick={() => setLang(code)} className={lang === code ? 'text-[#121212]' : 'text-[#121212]/40'} data-id="element-467">
                    {code}
                  </button>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}